import ky, { type HTTPError } from 'ky';
import { env } from '@/env';
import { tokenStorage } from '@/lib/auth/tokenStorage';
import { tenantInterceptor } from './tenantInterceptor';

// Shape of every error body our backend sends (RFC 7807 ProblemDetails).
export type ProblemDetails = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  errors?: { code: string; description: string; type: string }[];
};

// ky attaches this after the beforeError hook reads the body.
export type ApiHTTPError = HTTPError & { data?: ProblemDetails };

// Refresh-on-401 plumbing. The features/auth slice owns the actual refresh
// logic + session state; here we hold a lazily-registered callback so this
// module doesn't import from features/. main.tsx wires it via setRefreshHandler
// at app start.
type RefreshHandler = () => Promise<boolean>;
let refreshHandler: RefreshHandler | null = null;

export function setRefreshHandler(handler: RefreshHandler | null): void {
  refreshHandler = handler;
}

// Revocation-logout plumbing. When the backend returns Auth.MembershipRevoked
// (403) the caller's access was removed server-side; we clear the session and
// let ProtectedRoute redirect to the auth service. Registered by the auth
// feature slice so this module stays dependency-free from features/.
type LogoutHandler = (reason: string) => void;
let logoutHandler: LogoutHandler | null = null;

export function setLogoutHandler(handler: LogoutHandler | null): void {
  logoutHandler = handler;
}

export const apiClient = ky.create({
  prefixUrl: env.VITE_API_BASE_URL,
  timeout: 30_000,
  retry: { limit: 1 },
  hooks: {
    beforeError: [
      async (error: ApiHTTPError) => {
        try {
          // Clone so the body can still be read downstream if needed.
          const body = (await error.response.clone().json()) as ProblemDetails;
          error.data = body;
        } catch {
          // Non-JSON error body — leave error.data undefined.
        }
        return error;
      },
    ],
    beforeRequest: [
      (request) => {
        const token = tokenStorage.getAccessToken();
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
        tenantInterceptor(request);
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        // Detect membership-revoked 403 and trigger a clean logout so the
        // user sees "access revoked" rather than a generic forbidden banner.
        if (response.status === 403 && logoutHandler) {
          try {
            const body = (await response.clone().json()) as ProblemDetails;
            if (body?.errors?.some((e) => e.code === 'Auth.MembershipRevoked')) {
              logoutHandler('Your access to this organization has been revoked.');
              return response;
            }
          } catch {
            // Non-JSON body — not a revocation response, fall through.
          }
        }
        return response;
      },
      async (request, _options, response) => {
        if (response.status !== 401 || !refreshHandler) {
          return response;
        }

        // Guard against refresh on /auth/* endpoints themselves -- those
        // failing with 401 means credentials are wrong, not that the token
        // expired. Without this guard the client would loop trying to
        // refresh a non-existent session.
        const url = new URL(request.url);
        if (url.pathname.includes('/auth/')) {
          return response;
        }

        // Mark the request so we don't try to refresh twice on the same
        // call (a second 401 after retry means the new token is also bad).
        if (request.headers.get('X-Auth-Retried')) {
          return response;
        }

        const refreshed = await refreshHandler();
        if (!refreshed) {
          return response;
        }

        // Replay with the freshly-stored access token.
        const newToken = tokenStorage.getAccessToken();
        if (newToken) {
          request.headers.set('Authorization', `Bearer ${newToken}`);
          request.headers.set('X-Auth-Retried', '1');
        }
        return ky(request);
      },
    ],
  },
});
